// The one place the viewer decides whether a string from shared content may
// become a URL the browser acts on.
//
// Everything on this page that came from a share -- a widget's content, a desk
// projection, a document body, a captured widget -- was written by whoever
// shared it, and this origin also hosts sign-up and log-in forms. So a shared
// string is only ever turned into an href, a src, a srcset or a CSS url() after
// passing through here. Callers render plain text when a check returns null.
//
// Parsing is done with `new URL()`, the same WHATWG parser the browser uses
// when it follows the link. That is what makes the obfuscation tricks moot
// rather than something to enumerate: mixed case ("JaVaScRiPt:"), leading or
// trailing whitespace and C0 controls, and tabs or newlines inside the scheme
// ("java\tscript:") are all normalised by the parser exactly as the browser
// would normalise them, and we then look at the protocol it actually found.
// Callers must render the value these functions RETURN (the parser's
// serialisation), never the input, so what was checked is what is used.
//
// Entity-encoded input ("javascript&colon;", "&#106;avascript:") reaches this
// function either still encoded -- a React prop is set verbatim, never
// entity-decoded, so it cannot parse as an absolute URL and is refused -- or
// already decoded by the HTML parser in the sanitiser, in which case the
// protocol check sees the real scheme.
//
// Relative URLs are refused on purpose. A shared desk's content was authored
// somewhere else, so a relative reference resolves against THIS origin, never
// where its author meant: at best a dead link, at worst a link into the viewer
// itself (another share, the sign-up page) dressed as content. A
// protocol-relative "//host/path" is an absolute link to an arbitrary host
// wearing a relative disguise, and is refused for the same reason. Fragment
// references are allowed only where an SVG <use> needs one (see isFragmentRef).

/** Schemes a shared link may navigate to. tel: is not here: no widget emits it. */
const LINK_PROTOCOLS: ReadonlySet<string> = new Set(['http:', 'https:', 'mailto:'])

/** Schemes an image may load from. data: is narrowed to image types below. */
const IMAGE_PROTOCOLS: ReadonlySet<string> = new Set(['http:', 'https:', 'blob:', 'data:'])

/** data: URLs are only images, never documents: `data:image/<subtype>[;...],` */
const DATA_IMAGE = /^image\/[a-z0-9.+-]+[;,]/i

function parseAbsolute(raw: unknown): URL | null {
  if (typeof raw !== 'string' || raw === '') return null
  try {
    // No base on purpose: relative and protocol-relative input throws here.
    return new URL(raw)
  } catch {
    return null
  }
}

/**
 * Embedded credentials are refused. On a published desk they are the owner's
 * secret, and "https://plexiidesk.com@evil.example/" is the classic way to make
 * a link read as one host while going to another.
 */
function hasCredentials(u: URL): boolean {
  return u.username !== '' || u.password !== ''
}

/**
 * A URL a shared link may point at: absolute http, https or mailto, with no
 * embedded credentials. Returns the parser's serialisation, or null.
 */
export function safeHref(raw: unknown): string | null {
  const u = parseAbsolute(raw)
  if (!u || !LINK_PROTOCOLS.has(u.protocol) || hasCredentials(u)) return null
  return u.href
}

/**
 * A URL an image may load from: absolute http or https (no credentials), a
 * blob:, or a data: URL whose media type is an image. Image documents cannot
 * run script, so data:image/svg+xml is acceptable here and nowhere else.
 */
export function safeImageSrc(raw: unknown): string | null {
  const u = parseAbsolute(raw)
  if (!u || !IMAGE_PROTOCOLS.has(u.protocol)) return null
  if (u.protocol === 'data:') return DATA_IMAGE.test(u.pathname) ? u.href : null
  if (hasCredentials(u)) return null
  return u.href
}

/**
 * A srcset whose every candidate passes safeImageSrc, rebuilt from the parsed
 * candidates; null if any candidate fails or none exist. Parsing follows the
 * HTML "parse a srcset attribute" algorithm closely enough for validation: a
 * candidate's URL is a run of non-whitespace (trailing commas end it), and its
 * descriptors run to the next comma outside parentheses.
 */
export function safeImageSrcset(raw: unknown): string | null {
  if (typeof raw !== 'string') return null
  const out: string[] = []
  let i = 0
  const n = raw.length
  const isSpace = (c: string): boolean => c === ' ' || c === '\t' || c === '\n' || c === '\f' || c === '\r'
  while (i < n) {
    while (i < n && (isSpace(raw[i]) || raw[i] === ',')) i++
    if (i >= n) break
    const start = i
    while (i < n && !isSpace(raw[i])) i++
    let url = raw.slice(start, i)
    let descriptors = ''
    if (url.endsWith(',')) {
      url = url.replace(/,+$/, '')
    } else {
      const dStart = i
      let depth = 0
      while (i < n) {
        const c = raw[i]
        if (c === '(') depth++
        else if (c === ')') depth = Math.max(0, depth - 1)
        else if (c === ',' && depth === 0) break
        i++
      }
      descriptors = raw.slice(dStart, i).trim()
      i++ // past the comma
    }
    // Descriptors are only width/density tokens; anything else is refused
    // rather than passed through unexamined.
    if (descriptors !== '' && !/^\d+(\.\d+)?[wx]$|^\d+h$/i.test(descriptors)) return null
    const safe = safeImageSrc(url)
    if (!safe) return null
    out.push(descriptors ? `${safe} ${descriptors}` : safe)
  }
  return out.length ? out.join(', ') : null
}

/**
 * An in-document fragment reference such as an SVG <use href="#icon">. It can
 * only point at an element already on the page, never load or run anything.
 */
export function isFragmentRef(raw: unknown): boolean {
  return typeof raw === 'string' && /^#[A-Za-z_][\w.:-]{0,127}$/.test(raw)
}

/**
 * A plain CSS colour, for content that ends up in an inline style. Mirrors the
 * desktop's own safeColour (publicDeskProjection.ts), widened to the modern
 * colour functions. Function arguments may not contain parentheses, which is
 * what keeps url(), var(), image-set() and friends out: a colour value can then
 * only ever paint, never fetch.
 */
export function safeCssColor(raw: unknown): string | null {
  if (typeof raw !== 'string') return null
  const v = raw.trim()
  if (v === '' || v.length > 64) return null
  if (/^#(?:[0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(v)) return v
  if (/^(?:rgba?|hsla?|hwb|lab|lch|oklab|oklch)\(\s*[0-9a-z.\s,%/+-]*\)$/i.test(v)) return v
  if (/^[a-z]{3,30}$/i.test(v)) return v.toLowerCase()
  return null
}
